import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../../styles/pages/games/GeoRecall.module.scss";

const SCREENS = {
  HOME: "home",
  CONTINENTS: "continents",
  PLAY: "play",
  RESULTS: "results",
};

const DIFFICULTY = {
  easy: {
    label: "Easy",
    memorizeSec: 6,
    questionSec: 12,
    hitRadiusPx: 46,
    speedBonusMultiplier: 6,
  },
  normal: {
    label: "Normal",
    memorizeSec: 4,
    questionSec: 10,
    hitRadiusPx: 34,
    speedBonusMultiplier: 8,
  },
  hard: {
    label: "Hard",
    memorizeSec: 3,
    questionSec: 8,
    hitRadiusPx: 26,
    speedBonusMultiplier: 10,
  },
};

// Accent palette per your spec
const CONTINENT_META = [
  { key: "africa", label: "Africa", countriesCount: 12, accent: "#f59e0b" }, // amber/orange
  { key: "asia", label: "Asia", countriesCount: 12, accent: "#ef4444" }, // red/crimson
  { key: "europe", label: "Europe", countriesCount: 12, accent: "#3b82f6" }, // royal blue
  { key: "northAmerica", label: "North America", countriesCount: 10, accent: "#22c55e" }, // forest green
  { key: "southAmerica", label: "South America", countriesCount: 10, accent: "#14b8a6" }, // tropical teal
  { key: "oceania", label: "Oceania", countriesCount: 8, accent: "#06b6d4" }, // ocean cyan
];

// Hotspot-based country sets (starter dataset)
// x,y are percentages relative to map frame (0..100).
const CONTINENTS_DATA = {
  africa: {
    mapFill: "#7a4a23",
    glow: "rgba(245, 158, 11, 0.22)",
    countries: [
      { id: "ma", name: "Morocco", x: 32, y: 24 },
      { id: "dz", name: "Algeria", x: 43, y: 30 },
      { id: "ly", name: "Libya", x: 56, y: 30 },
      { id: "eg", name: "Egypt", x: 70, y: 28 },
      { id: "sd", name: "Sudan", x: 69, y: 43 },
      { id: "et", name: "Ethiopia", x: 79, y: 53 },
      { id: "ke", name: "Kenya", x: 74, y: 63 },
      { id: "tz", name: "Tanzania", x: 76, y: 72 },
      { id: "gh", name: "Ghana", x: 33, y: 55 },
      { id: "ng", name: "Nigeria", x: 41, y: 56 },
      { id: "cd", name: "Congo", x: 56, y: 70 },
      { id: "za", name: "South Africa", x: 56, y: 90 },
    ],
  },

  asia: {
    mapFill: "#3a1c25",
    glow: "rgba(239, 68, 68, 0.18)",
    countries: [
      { id: "tr", name: "Turkey", x: 28, y: 32 },
      { id: "sa", name: "Saudi Arabia", x: 40, y: 50 },
      { id: "ir", name: "Iran", x: 45, y: 42 },
      { id: "in", name: "India", x: 56, y: 58 },
      { id: "pk", name: "Pakistan", x: 50, y: 52 },
      { id: "cn", name: "China", x: 68, y: 46 },
      { id: "jp", name: "Japan", x: 84, y: 48 },
      { id: "kr", name: "South Korea", x: 82, y: 44 },
      { id: "th", name: "Thailand", x: 70, y: 66 },
      { id: "vn", name: "Vietnam", x: 74, y: 66 },
      { id: "id", name: "Indonesia", x: 74, y: 78 },
      { id: "ph", name: "Philippines", x: 82, y: 64 },
    ],
  },

  europe: {
    mapFill: "#0f2a55",
    glow: "rgba(59, 130, 246, 0.18)",
    countries: [
      { id: "gb", name: "United Kingdom", x: 27, y: 30 },
      { id: "ie", name: "Ireland", x: 23, y: 35 },
      { id: "fr", name: "France", x: 34, y: 48 },
      { id: "es", name: "Spain", x: 30, y: 62 },
      { id: "pt", name: "Portugal", x: 24, y: 64 },
      { id: "de", name: "Germany", x: 44, y: 42 },
      { id: "it", name: "Italy", x: 46, y: 60 },
      { id: "nl", name: "Netherlands", x: 40, y: 38 },
      { id: "pl", name: "Poland", x: 55, y: 42 },
      { id: "se", name: "Sweden", x: 48, y: 24 },
      { id: "no", name: "Norway", x: 42, y: 20 },
      { id: "gr", name: "Greece", x: 56, y: 70 },
    ],
  },

  northAmerica: {
    mapFill: "#0f3a2a",
    glow: "rgba(34, 197, 94, 0.16)",
    countries: [
      { id: "ca", name: "Canada", x: 46, y: 22 },
      { id: "us", name: "United States", x: 46, y: 42 },
      { id: "mx", name: "Mexico", x: 41, y: 58 },
      { id: "gt", name: "Guatemala", x: 44, y: 68 },
      { id: "cu", name: "Cuba", x: 56, y: 62 },
      { id: "ht", name: "Haiti", x: 61, y: 66 },
      { id: "do", name: "Dominican Rep.", x: 64, y: 66 },
      { id: "pa", name: "Panama", x: 52, y: 74 },
      { id: "jm", name: "Jamaica", x: 58, y: 70 },
      { id: "cr", name: "Costa Rica", x: 50, y: 78 },
    ],
  },

  southAmerica: {
    mapFill: "#0f3a3a",
    glow: "rgba(20, 184, 166, 0.16)",
    countries: [
      { id: "co", name: "Colombia", x: 46, y: 18 },
      { id: "ve", name: "Venezuela", x: 56, y: 18 },
      { id: "pe", name: "Peru", x: 42, y: 40 },
      { id: "ec", name: "Ecuador", x: 40, y: 26 },
      { id: "br", name: "Brazil", x: 62, y: 44 },
      { id: "bo", name: "Bolivia", x: 50, y: 52 },
      { id: "py", name: "Paraguay", x: 56, y: 62 },
      { id: "cl", name: "Chile", x: 38, y: 68 },
      { id: "ar", name: "Argentina", x: 50, y: 78 },
      { id: "uy", name: "Uruguay", x: 58, y: 78 },
    ],
  },

  oceania: {
    mapFill: "#083548",
    glow: "rgba(6, 182, 212, 0.18)",
    countries: [
      { id: "au", name: "Australia", x: 62, y: 58 },
      { id: "nz", name: "New Zealand", x: 78, y: 72 },
      { id: "pg", name: "Papua New Guinea", x: 66, y: 40 },
      { id: "fj", name: "Fiji", x: 82, y: 50 },
      { id: "sb", name: "Solomon Islands", x: 74, y: 42 },
      { id: "vu", name: "Vanuatu", x: 78, y: 46 },
      { id: "ws", name: "Samoa", x: 86, y: 40 },
      { id: "nc", name: "New Caledonia", x: 76, y: 56 },
    ],
  },
};

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function formatTime(s) {
  const ss = Math.max(0, s);
  const m = Math.floor(ss / 60);
  const r = ss % 60;
  return m > 0 ? `${m}:${String(r).padStart(2, "0")}` : `${r}`;
}

function useBeep(soundOn) {
  const ctxRef = useRef(null);

  const beep = (type) => {
    if (!soundOn) return;

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      if (!ctxRef.current) ctxRef.current = new AudioCtx();
      const ctx = ctxRef.current;

      const o = ctx.createOscillator();
      const g = ctx.createGain();

      // simple tones
      const freq = type === "good" ? 660 : type === "bad" ? 220 : 440;
      o.frequency.value = freq;
      o.type = "sine";

      g.gain.value = 0.0001;
      o.connect(g);
      g.connect(ctx.destination);

      const now = ctx.currentTime;
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(0.10, now + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);

      o.start(now);
      o.stop(now + 0.2);
    } catch {
      // ignore audio errors
    }
  };

  return beep;
}

function ContinentBlob({ accent }) {
  // Small decorative blob for cards
  return (
    <svg className={styles.cardBlob} viewBox="0 0 220 140" aria-hidden="true">
      <defs>
        <radialGradient id="g" cx="40%" cy="30%" r="80%">
          <stop offset="0%" stopColor={accent} stopOpacity="0.50" />
          <stop offset="100%" stopColor={accent} stopOpacity="0.18" />
        </radialGradient>
      </defs>
      <path
        d="M44,78 C36,55 52,26 86,18 C112,12 130,24 144,30 C160,38 190,40 198,64
           C206,90 190,108 162,116 C134,124 118,132 92,126 C68,120 52,104 44,78 Z"
        fill="url(#g)"
      />
    </svg>
  );
}

function ContinentSilhouette({ continentKey, accent }) {
  // Stylized silhouettes (not accurate geography yet).
  // Swap these paths later with real continent SVG.
  const silhouettes = {
    africa: "M120,20 C150,10 190,18 210,40 C235,68 240,90 230,120 C222,145 212,160 200,176 C188,192 174,208 168,226 C160,250 150,280 136,306 C124,332 92,330 74,312 C50,286 52,252 58,226 C64,200 56,170 52,144 C46,112 50,84 66,60 C84,32 98,26 120,20 Z",
    asia: "M52,56 C86,24 136,18 176,30 C206,38 230,54 240,78 C250,106 238,134 218,150 C206,160 198,170 182,186 C168,200 162,220 140,234 C118,248 96,248 74,232 C50,214 42,176 40,148 C38,114 26,82 52,56 Z",
    europe: "M72,56 C96,30 138,26 170,38 C196,48 214,70 214,94 C214,122 194,140 174,152 C156,162 150,176 132,186 C110,198 88,192 72,182 C48,166 42,132 46,106 C52,82 52,76 72,56 Z",
    northAmerica: "M68,44 C102,20 148,20 178,34 C210,48 234,74 226,102 C220,126 196,130 182,146 C168,162 162,186 140,198 C114,212 90,208 66,192 C44,178 34,150 34,118 C34,82 36,66 68,44 Z",
    southAmerica: "M120,26 C150,30 168,60 166,92 C164,122 150,132 142,154 C132,184 140,206 124,230 C110,250 92,256 78,240 C60,220 70,196 62,170 C52,140 38,116 46,88 C54,58 90,22 120,26 Z",
    oceania: "M70,62 C96,40 134,36 166,46 C194,54 206,74 200,96 C194,120 172,128 156,136 C132,148 112,154 92,148 C64,140 42,120 40,96 C38,78 54,74 70,62 Z",
  };

  const path = silhouettes[continentKey] ?? silhouettes.africa;

  return (
    <svg className={styles.mapSvg} viewBox="0 0 260 340" aria-hidden="true">
      <defs>
        <radialGradient id="mapGrad" cx="40%" cy="25%" r="85%">
          <stop offset="0%" stopColor={accent} stopOpacity="0.22" />
          <stop offset="100%" stopColor="#000" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect x="0" y="0" width="260" height="340" fill="url(#mapGrad)" />
      <path d={path} className={styles.mapShape} />
    </svg>
  );
}

export default function GeoRecall() {
  const [screen, setScreen] = useState(SCREENS.HOME);

  const [difficultyKey, setDifficultyKey] = useState("normal");
  const difficulty = DIFFICULTY[difficultyKey];

  const [soundOn, setSoundOn] = useState(true);
  const beep = useBeep(soundOn);

  const [continentKey, setContinentKey] = useState(null);

  const continentMeta = useMemo(
    () => CONTINENT_META.find((c) => c.key === continentKey) || null,
    [continentKey]
  );
  const continentData = continentKey ? CONTINENTS_DATA[continentKey] : null;

  // Play state
  const [phase, setPhase] = useState("memorize"); // memorize | challenge
  const [timer, setTimer] = useState(0);

  const [queue, setQueue] = useState([]); // countries in order
  const [idx, setIdx] = useState(0);

  const [locked, setLocked] = useState(false);

  const [points, setPoints] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);

  const [toast, setToast] = useState(null); // {type, text}

  const [clickMark, setClickMark] = useState(null); // {x,y,type,label}
  const [revealMark, setRevealMark] = useState(null); // {x,y,label}

  const mapRef = useRef(null);

  const currentTarget = queue[idx] || null;

  const accent = continentMeta?.accent ?? "#7c3aed"; // fallback purple

  const progress = queue.length ? (idx / queue.length) : 0;

  // Start memorize/challenge timers
  useEffect(() => {
    if (screen !== SCREENS.PLAY) return;
    const t = setInterval(() => {
      setTimer((s) => s - 1);
    }, 1000);
    return () => clearInterval(t);
  }, [screen]);

  // Timer transitions
  useEffect(() => {
    if (screen !== SCREENS.PLAY) return;
    if (timer > 0) return;

    if (phase === "memorize") {
      setPhase("challenge");
      setTimer(difficulty.questionSec);
      setToast(null);
      setClickMark(null);
      setRevealMark(null);
      return;
    }

    if (phase === "challenge") {
      // timeout counts as wrong
      if (currentTarget) {
        setWrong((w) => w + 1);
        beep("bad");
        setToast({ type: "bad", text: `Time! That was ${currentTarget.name}.` });
        setRevealMark({ x: currentTarget.x, y: currentTarget.y, label: currentTarget.name });

        // next after short pause
        setLocked(true);
        window.setTimeout(() => {
          goNext();
        }, 800);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer]);

  function resetGameToHome() {
    setScreen(SCREENS.HOME);
    setContinentKey(null);

    setPhase("memorize");
    setTimer(0);
    setQueue([]);
    setIdx(0);

    setLocked(false);
    setPoints(0);
    setCorrect(0);
    setWrong(0);

    setToast(null);
    setClickMark(null);
    setRevealMark(null);
  }

  function startFromHome() {
    setScreen(SCREENS.CONTINENTS);
  }

  function selectContinent(key) {
    setContinentKey(key);

    const list = CONTINENTS_DATA[key]?.countries ?? [];
    // keep order randomized
    const q = shuffle(list);

    setQueue(q);
    setIdx(0);

    setPoints(0);
    setCorrect(0);
    setWrong(0);

    setPhase("memorize");
    setTimer(difficulty.memorizeSec);
    setLocked(false);

    setToast({ type: "info", text: "Memorize the labels..." });
    setClickMark(null);
    setRevealMark(null);

    setScreen(SCREENS.PLAY);
  }

  function goBackToContinents() {
    setScreen(SCREENS.CONTINENTS);
    setContinentKey(null);
  }

  function goNext() {
    setLocked(false);
    setClickMark(null);
    setRevealMark(null);
    setToast(null);

    const next = idx + 1;
    if (next >= queue.length) {
      setScreen(SCREENS.RESULTS);
      return;
    }
    setIdx(next);
    setTimer(difficulty.questionSec);
  }

  function handleMapClick(e) {
    if (screen !== SCREENS.PLAY) return;
    if (phase !== "challenge") return;
    if (locked) return;
    if (!currentTarget) return;

    const el = mapRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const cx = clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100);
    const cy = clamp(((e.clientY - rect.top) / rect.height) * 100, 0, 100);

    // find nearest hotspot in pixels
    const list = queue;
    let best = null;

    for (const c of list) {
      const dx = ((c.x - cx) / 100) * rect.width;
      const dy = ((c.y - cy) / 100) * rect.height;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (!best || dist < best.dist) best = { c, dist };
    }

    const hitRadius = difficulty.hitRadiusPx;
    const clickedCountry = best && best.dist <= hitRadius ? best.c : null;

    // lock + mark click
    setLocked(true);

    if (!clickedCountry) {
      // clicked nowhere near a hotspot
      setWrong((w) => w + 1);
      beep("bad");
      setClickMark({ x: cx, y: cy, type: "bad", label: "×" });
      setRevealMark({ x: currentTarget.x, y: currentTarget.y, label: currentTarget.name });
      setToast({ type: "bad", text: `Miss — correct was ${currentTarget.name}.` });

      window.setTimeout(() => goNext(), 900);
      return;
    }

    const isCorrect = clickedCountry.id === currentTarget.id;

    if (isCorrect) {
      // scoring: base + speed bonus
      const speedBonus = Math.floor(timer * difficulty.speedBonusMultiplier);
      const gained = 100 + speedBonus;
      setPoints((p) => p + gained);
      setCorrect((c) => c + 1);

      beep("good");
      setClickMark({ x: clickedCountry.x, y: clickedCountry.y, type: "good", label: "✓" });
      setToast({ type: "good", text: `Correct! +${gained} points` });

      window.setTimeout(() => goNext(), 750);
    } else {
      setWrong((w) => w + 1);
      beep("bad");
      setClickMark({ x: clickedCountry.x, y: clickedCountry.y, type: "bad", label: "×" });
      setRevealMark({ x: currentTarget.x, y: currentTarget.y, label: currentTarget.name });
      setToast({
        type: "bad",
        text: `Wrong — that was ${clickedCountry.name}. Correct: ${currentTarget.name}.`,
      });

      window.setTimeout(() => goNext(), 950);
    }
  }

  const countryCountLabel = continentMeta?.countriesCount ?? (continentData?.countries?.length ?? 0);

  return (
    <div className={styles.root} style={{ ["--accent"]: accent }}>
      <div className={styles.bgGlow} />

      {/* HOME */}
      {screen === SCREENS.HOME && (
        <div className={styles.centerStage}>
          <div className={styles.brand}>
            <div className={styles.logoMark}>
              <svg viewBox="0 0 32 32" aria-hidden="true">
                <path
                  d="M16 3c7.18 0 13 5.82 13 13s-5.82 13-13 13S3 23.18 3 16 8.82 3 16 3Z"
                  fill="url(#lg)"
                />
                <path
                  d="M9.5 16c0-4 2.6-7.2 6.5-8.2M22.5 16c0 4-2.6 7.2-6.5 8.2"
                  stroke="white"
                  strokeOpacity="0.9"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  fill="none"
                />
                <path
                  d="M6.8 12.7c2.1 1.2 5.6 2.2 9.2 2.2 3.6 0 7.1-1 9.2-2.2"
                  stroke="white"
                  strokeOpacity="0.85"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  fill="none"
                />
                <path
                  d="M6.8 19.3c2.1-1.2 5.6-2.2 9.2-2.2 3.6 0 7.1 1 9.2 2.2"
                  stroke="white"
                  strokeOpacity="0.85"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  fill="none"
                />
                <defs>
                  <linearGradient id="lg" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#3b82f6" />
                    <stop offset="1" stopColor="#a855f7" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            <h1 className={styles.title}>GeoRecall</h1>
            <p className={styles.subtitle}>
              Master world geography through interactive memory <br />
              challenges
            </p>
          </div>

          <div className={styles.panel}>
            <div className={styles.panelLabel}>Select Difficulty</div>
            <div className={styles.segmented}>
              {Object.entries(DIFFICULTY).map(([key, cfg]) => (
                <button
                  key={key}
                  type="button"
                  className={[
                    styles.segment,
                    key === difficultyKey ? styles.segmentActive : "",
                  ].join(" ")}
                  onClick={() => setDifficultyKey(key)}
                >
                  {cfg.label}
                </button>
              ))}
            </div>

            <button type="button" className={styles.primaryCta} onClick={startFromHome}>
              <span>Start Game</span>
              <span className={styles.ctaArrow}>›</span>
            </button>

            <button
              type="button"
              className={styles.soundToggle}
              onClick={() => setSoundOn((s) => !s)}
              aria-pressed={soundOn}
            >
              <span className={styles.soundIcon} aria-hidden="true">
                {soundOn ? "🔊" : "🔇"}
              </span>
              <span>{soundOn ? "Sound On" : "Sound Off"}</span>
            </button>
          </div>
        </div>
      )}

      {/* CONTINENTS */}
      {screen === SCREENS.CONTINENTS && (
        <div className={styles.page}>
          <div className={styles.pageHeader}>
            <h2 className={styles.pageTitle}>Choose Your Continent</h2>
            <p className={styles.pageSubtitle}>
              Select a region to test your geography knowledge
            </p>
          </div>

          <div className={styles.grid}>
            {CONTINENT_META.map((c) => (
              <button
                key={c.key}
                type="button"
                className={styles.continentCard}
                style={{ ["--accent"]: c.accent }}
                onClick={() => selectContinent(c.key)}
              >
                <div className={styles.cardTop}>
                  <div className={styles.pin}>
                    <span className={styles.pinDot} />
                  </div>

                  <div className={styles.cardTitleWrap}>
                    <div className={styles.cardTitle}>{c.label}</div>
                    <div className={styles.cardMeta}>{c.countriesCount} countries</div>
                  </div>
                </div>

                <div className={styles.cardCenter}>
                  <ContinentBlob accent={c.accent} />
                </div>

                <div className={styles.cardBottom}>
                  <span className={styles.cardStart}>Start Challenge</span>
                  <span className={styles.cardChevron}>›</span>
                </div>
              </button>
            ))}
          </div>

          <button type="button" className={styles.backLink} onClick={resetGameToHome}>
            ↩ Back to Menu
          </button>
        </div>
      )}

      {/* PLAY */}
      {screen === SCREENS.PLAY && continentKey && continentData && (
        <div className={styles.playPage}>
          <div className={styles.playHud}>
            <div className={styles.hudLeft}>
              <div className={styles.pill}>
                <span className={styles.pillIcon} aria-hidden="true">🏆</span>
                <span className={styles.pillText}>{points}</span>
              </div>

              <div className={styles.pill}>
                <span className={styles.pillIcon} aria-hidden="true">✅</span>
                <span className={styles.pillText}>{correct}</span>
              </div>

              <div className={styles.pill}>
                <span className={styles.pillIcon} aria-hidden="true">❌</span>
                <span className={styles.pillText}>{wrong}</span>
              </div>
            </div>

            <div className={styles.hudRight}>
              <div className={styles.pill}>
                <span className={styles.pillIcon} aria-hidden="true">⏱</span>
                <span className={styles.pillText}>{formatTime(timer)}</span>
              </div>

              <button
                type="button"
                className={styles.smallBtn}
                onClick={() => setSoundOn((s) => !s)}
                aria-pressed={soundOn}
                title="Toggle sound"
              >
                {soundOn ? "🔊" : "🔇"}
              </button>

              <button type="button" className={styles.smallBtn} onClick={goBackToContinents} title="Back">
                ⟵
              </button>
            </div>
          </div>

          <div className={styles.playPrompt}>
            {phase === "memorize" ? (
              <>
                <div className={styles.promptLabel}>Learning Phase</div>
                <div className={styles.promptMain}>Memorizing...</div>
              </>
            ) : (
              <>
                <div className={styles.promptLabel}>Find this country:</div>
                <div className={styles.promptMain}>{currentTarget?.name ?? "—"}</div>
              </>
            )}
          </div>

          <div className={styles.progressWrap}>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} style={{ width: `${progress * 100}%` }} />
            </div>
            <div className={styles.progressText}>
              {idx + 1} / {queue.length}
            </div>
          </div>

          <div
            className={styles.mapFrame}
            style={{
              ["--mapGlow"]: continentData.glow,
              ["--mapFill"]: continentData.mapFill,
            }}
            onClick={handleMapClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                // no-op (click is mouse-driven)
              }
            }}
            ref={mapRef}
          >
            <div className={styles.mapBg} />
            <div className={styles.mapInner}>
              <ContinentSilhouette continentKey={continentKey} accent={accent} />

              {/* Hotspots */}
              <div className={styles.hotspots}>
                {queue.map((c) => (
                  <div
                    key={c.id}
                    className={[
                      styles.hotspot,
                      phase === "memorize" ? styles.hotspotVisible : styles.hotspotHidden,
                    ].join(" ")}
                    style={{ left: `${c.x}%`, top: `${c.y}%` }}
                  >
                    <div className={styles.dot} />
                    {phase === "memorize" && (
                      <div className={styles.tag}>{c.name}</div>
                    )}
                  </div>
                ))}
              </div>

              {/* Click marker */}
              {clickMark && (
                <div
                  className={[
                    styles.clickMark,
                    clickMark.type === "good" ? styles.markGood : styles.markBad,
                  ].join(" ")}
                  style={{ left: `${clickMark.x}%`, top: `${clickMark.y}%` }}
                >
                  <span>{clickMark.label}</span>
                </div>
              )}

              {/* Reveal correct location when wrong/timeout */}
              {revealMark && (
                <div
                  className={styles.reveal}
                  style={{ left: `${revealMark.x}%`, top: `${revealMark.y}%` }}
                >
                  <div className={styles.revealPin} />
                  <div className={styles.revealText}>{revealMark.label}</div>
                </div>
              )}
            </div>

            <div className={styles.phaseFooter}>
              {phase === "memorize" ? (
                <span className={styles.footerHint}>⚡ Memorizing...</span>
              ) : (
                <span className={styles.footerHint}>Click the correct location</span>
              )}
            </div>
          </div>

          {toast && (
            <div
              className={[
                styles.toast,
                toast.type === "good" ? styles.toastGood :
                toast.type === "bad" ? styles.toastBad :
                styles.toastInfo,
              ].join(" ")}
              role="status"
            >
              {toast.text}
            </div>
          )}
        </div>
      )}

      {/* RESULTS */}
      {screen === SCREENS.RESULTS && (
        <div className={styles.centerStage}>
          <div className={styles.resultsCard}>
            <div className={styles.resultsTop}>
              <div className={styles.resultsBadge}>Session Results</div>
              <div className={styles.resultsTitle}>
                {continentMeta?.label ?? "Continent"} — {DIFFICULTY[difficultyKey].label}
              </div>
            </div>

            <div className={styles.resultsStats}>
              <div className={styles.stat}>
                <div className={styles.statLabel}>Points</div>
                <div className={styles.statValue}>{points}</div>
              </div>
              <div className={styles.stat}>
                <div className={styles.statLabel}>Correct</div>
                <div className={styles.statValue}>{correct}</div>
              </div>
              <div className={styles.stat}>
                <div className={styles.statLabel}>Wrong</div>
                <div className={styles.statValue}>{wrong}</div>
              </div>
              <div className={styles.stat}>
                <div className={styles.statLabel}>Accuracy</div>
                <div className={styles.statValue}>
                  {Math.round((correct / Math.max(1, correct + wrong)) * 100)}%
                </div>
              </div>
            </div>

            <div className={styles.resultsActions}>
              <button
                type="button"
                className={styles.primaryCta}
                onClick={() => {
                  if (!continentKey) return resetGameToHome();
                  selectContinent(continentKey);
                }}
              >
                <span>Play Again</span>
                <span className={styles.ctaArrow}>›</span>
              </button>

              <button type="button" className={styles.secondaryBtn} onClick={goBackToContinents}>
                Choose Another Continent
              </button>

              <button type="button" className={styles.ghostBtn} onClick={resetGameToHome}>
                Back to Menu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
